"use client";

import { useState, useEffect, useRef } from "react";
import type { MultiSelectOptionT } from "@/app/components/ui/multi-select";

type UseVisibleCountPropsT = {
  selectedOptions: MultiSelectOptionT[];
};

/**
 * Custom hook for calculating visible badge count in multi-select
 *
 * Dynamically calculates how many option badges can fit in the container
 * based on actual space requirements and available width. No artificial
 * maximum limits - only shows +N badge when space is actually insufficient.
 *
 * @param props - Hook configuration
 * @returns Container ref and visible count
 *
 * @example
 * ```typescript
 * const { containerRef, visibleCount } = useVisibleCount({
 *   selectedOptions
 * });
 *
 * <div ref={containerRef}>
 *   {selectedOptions.slice(0, visibleCount).map(...)}
 *   {selectedOptions.length > visibleCount && (
 *     <div>+{selectedOptions.length - visibleCount}</div>
 *   )}
 * </div>
 * ```
 */
export function useVisibleCount({ selectedOptions }: UseVisibleCountPropsT) {
  // ✅ Initialize with selectedOptions.length to prevent incorrect +N badge on mount/prefill
  const [visibleCount, setVisibleCount] = useState(selectedOptions.length);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate how many badges can fit based on actual space requirements
  useEffect(() => {
    const calculateVisibleCount = () => {
      if (!containerRef.current || selectedOptions.length === 0) {
        setVisibleCount(selectedOptions.length);
        return;
      }

      const container = containerRef.current;
      const containerWidth = container.offsetWidth;

      // ✅ Validate container width - if not yet rendered, use optimistic default
      // This prevents incorrect calculations when component is prefilled before being visible
      if (containerWidth === 0 || containerWidth < 100) {
        // Container not yet rendered or too small - show all badges optimistically
        // Will recalculate once container is properly rendered
        setVisibleCount(selectedOptions.length);
        return;
      }

      // Reserve space for chevron icon and potential +N badge
      const reservedSpace = 80; // Chevron + clear button + padding + potential +N badge space
      const availableWidth = containerWidth - reservedSpace;

      // ✅ Ensure availableWidth is positive before calculation
      if (availableWidth <= 0) {
        setVisibleCount(selectedOptions.length);
        return;
      }

      // Calculate cumulative width of badges dynamically
      let cumulativeWidth = 0;
      let visibleBadgeCount = 0;

      for (let i = 0; i < selectedOptions.length; i++) {
        const option = selectedOptions[i];

        // Estimate badge width based on text length and styling
        // Base: 24px padding (12px left + 12px right) + 16px X button + 4px gap
        const baseWidth = 44;

        // Estimate text width: ~8px per character (conservative estimate for text-xs)
        const textWidth = Math.min(option.label.length * 8, 80); // Max 80px due to truncation

        const badgeWidth = baseWidth + textWidth;

        if (cumulativeWidth + badgeWidth <= availableWidth) {
          cumulativeWidth += badgeWidth;
          visibleBadgeCount++;
        } else {
          // If this badge doesn't fit, check if we need to reserve space for +N badge
          if (i < selectedOptions.length - 1) {
            // More badges remaining, need space for +N badge (~40px)
            if (
              visibleBadgeCount > 0 &&
              cumulativeWidth + 40 > availableWidth
            ) {
              visibleBadgeCount = Math.max(1, visibleBadgeCount - 1);
            }
          }
          break;
        }
      }

      const finalCount = visibleBadgeCount;

      setVisibleCount(finalCount);
    };

    // ✅ Use requestAnimationFrame for better timing - ensures DOM is fully rendered
    // This is especially important when component is prefilled before being visible
    let rafId: number | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    rafId = requestAnimationFrame(() => {
      // Small additional delay to ensure layout is complete
      timeoutId = setTimeout(calculateVisibleCount, 10);
    });

    // ✅ Use ResizeObserver to detect container size changes
    // This is crucial when component becomes visible after being hidden (e.g., prefilled form)
    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          calculateVisibleCount();
        });
      });
      resizeObserver.observe(containerRef.current);
    }

    // Recalculate on window resize
    const handleResize = () => {
      requestAnimationFrame(() => {
        calculateVisibleCount();
      });
    };
    window.addEventListener("resize", handleResize);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [selectedOptions]);

  return {
    containerRef,
    visibleCount,
  };
}
